import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useOktaAuth } from '@okta/okta-react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { getPageTitle } from '../../utils/pageTitles';
import api from '../../services';
import { isAuthDisabled } from '../../utils/authStateHelpers';
import ErrorBoundary from '../../components/ErrorBoundary/ErrorBoundary';
import { ZuulToaster } from '../../components/Toaster/ZuulToaster';
import AppAside from './AppAside';
import AppHeader from './AppHeader';
import AppRoutes from './AppRoutes';
import AppSidebar from './AppSidebar';

const loading = (
  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
    <CircularProgress />
  </Box>
);

interface LoginResponse {
  zuulUser?: { zuulUserRole?: { name?: string } };
}

const registerLogin = (userData: { email: string; realname: string; lastLogin: Date }, signal: AbortSignal) =>
  api
    .put('zuulusers/login', userData, { signal })
    .then((response: { data: LoginResponse }) => {
      localStorage.setItem('userData', JSON.stringify(response.data.zuulUser));
    })
    .catch((err: { code?: string }) => {
      if (err.code === 'ERR_CANCELED') return;
      console.error('LOGIN ERROR', err);
    });

/** Authenticated shell: sidebar + header + routed content + right-hand aside. */
export default function DefaultLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, oktaAuth } = useOktaAuth();

  const signOut = () => {
    void oktaAuth.signOut();
    localStorage.removeItem('userData');
    navigate('/login');
  };

  React.useEffect(() => {
    const controller = new AbortController();

    if (isAuthDisabled()) {
      // Dev-only: bypass Okta and seed a local dev user so saves carry a real
      // zuulUser row. Requires local Zuul running with zuul.whatif-admin.http-auth=false.
      void registerLogin(
        { email: 'localdev@whatifmediagroup.com', realname: 'Local Dev', lastLogin: new Date() },
        controller.signal
      );
    } else if (authState?.isAuthenticated && authState.accessToken?.accessToken) {
      api.defaults.headers.common.Authorization = `Bearer ${authState.accessToken.accessToken}`;
      oktaAuth
        .getUser()
        .then((info) =>
          registerLogin(
            { email: info.email ?? '', realname: info.name ?? '', lastLogin: new Date() },
            controller.signal
          )
        )
        .catch((err: unknown) => {
          console.error('Failed to get user info:', err);
        });
    }

    return () => controller.abort();
  }, [authState, oktaAuth]);

  if (!isAuthDisabled() && authState !== null && !authState.isAuthenticated) {
    const currentUrl = location.pathname + location.search;
    if (currentUrl !== '/login' && currentUrl !== '/login/callback') {
      sessionStorage.setItem('preLoginUrl', currentUrl);
    }
    return <Navigate to='/login' replace />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Helmet>
        <title>{getPageTitle(location.pathname, '')}</title>
      </Helmet>
      <AppSidebar />
      <Box component='main' sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppHeader onLogout={signOut} />
        <Box
          sx={{
            flex: 1,
            p: 3,
            // Page rhythm: two cards stacked on a page never touch. Pages wrap their cards in
            // whatever they like — nothing, a <form>, an `animated fadeIn` div, or a div AND a
            // form — so this matches by what a card IS rather than by how deep it sits. Two
            // conditions carry it: `:not(.MuiPaper-root *)` keeps it to a page's own cards and
            // off the ones a component nests inside its own layout, and the parent exclusion
            // leaves Stack and Grid alone because they already space what they contain.
            [[
              '& > .MuiPaper-root + .MuiPaper-root',
              '& :not(.MuiStack-root, .MuiGrid-root) > .MuiPaper-root + .MuiPaper-root:not(.MuiPaper-root *)',
            ].join(', ')]: { mt: 3 },
            // A page that ends in a StickyActionBar gets its bar at the bottom of the screen even
            // when the form is too short to scroll. `:has` picks out exactly the wrappers between
            // this box and the bar — a form, a div, whatever a page happens to nest — and makes
            // each one a stretched flex column, so the bar's spacer can take up the slack. Pages
            // without a bar are untouched, which is why this is two selectors rather than a rule
            // on every page.
            '&:has([data-sticky-action-bar])': { display: 'flex', flexDirection: 'column' },
            '& :has([data-sticky-action-bar])': {
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
            },
          }}
        >
          <React.Fragment key={location.pathname}>
            <ErrorBoundary message='Error loading route component'>
              <React.Suspense fallback={loading}>
                <AppRoutes />
              </React.Suspense>
            </ErrorBoundary>
          </React.Fragment>
        </Box>
      </Box>
      <AppAside />
      {/* Single app-wide toast host: a second <Toaster> would render every toast twice. */}
      <ZuulToaster />
    </Box>
  );
}
